import * as React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { InputProps, Option } from 'interfaces';
import clsx from 'clsx';
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/24/solid';
import { CypressI } from 'interfaces/cypress';
import { ErrorMessageInput, Icon, Spinner } from '@/components/common';
import { Asterisk } from 'lucide-react';
import { InputText } from '../input-text';
import { useForm } from 'react-hook-form';
import Icons from '@/const/icons';

import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerDescription,
} from '@/components/ui/drawer';
import { useMediaQuery } from 'react-responsive';

export interface InputListProps {
	options: Option[];
	handleChange?: (value: string) => void;
	myDefaultValue?: string;
	myDefaultValueOption?: Option;
	loading?: boolean;
	resetValue?: boolean;
	markAsRequired?: boolean;
	maxHeight?: string;
	isSearch?: boolean;
	from?: string;
	showActionButtons?: boolean;
	setInputCreate?: (
		value: string,
		func: any,
		id?: string | number | undefined
	) => void;
	setInputDelete?: (id: string) => void;
	resetValueAfterSelecting?: boolean;
	classNameList?: string;
	classNameLabel?: string;
	containerRef?: (node: HTMLDivElement | null) => void;
	onToggleOpen?: (isOpen: boolean) => void;
	handleSearch?: (value: string) => void;
	dynamicContainerWidth?: number;
	withOutErrorIcon?: boolean;
	clearable?: boolean;
	inline?: boolean;
	classNameInlineList?: string;
}

const getDefaultValue = (
	options: Option[],
	value: string | undefined,
	label?: string | undefined,
	title?: string,
	inputPlaceholder?: string
): Option => {
	if (value !== undefined && value !== null && value !== '') {
		const foundOption = options.find((opt) => opt.name === value);
		if (foundOption) return foundOption;
		return { name: value, label: label ? label : '', placeholder: false };
	}
	if (options.length > 0 && options[0]) return options[0];
	const labelText =
		inputPlaceholder || (title ? `Select ${title}` : 'Select an option');
	return { name: '', label: labelText, placeholder: true };
};

export const InputList: React.FC<
	InputProps &
		InputListProps &
		CypressI &
		React.InputHTMLAttributes<HTMLInputElement>
> = ({
	options,
	myDefaultValue,
	myDefaultValueOption,
	className,
	handleChange,
	setValueInput,
	classNameInput,
	classNameTitle,
	classNameList,
	classNameLabel,
	loading,
	resetValue,
	markAsRequired,
	maxHeight,
	isSearch,
	setInputCreate,
	resetValueAfterSelecting,
	setInputDelete,
	showActionButtons = false,
	containerRef,
	onToggleOpen,
	handleSearch,
	from,
	dynamicContainerWidth,
	withOutErrorIcon,
	clearable,
	inline,
	classNameInlineList,
	...props
}) => {
	const isMobile = useMediaQuery({
		query: '(max-width: 767px)',
	});
	const [drawerOpen, setDrawerOpen] = React.useState(false);

	const inputRef = React.useRef<HTMLInputElement>(null);
	const getInitialSelected = (): Option => {
		if (
			myDefaultValue !== undefined &&
			myDefaultValue !== null &&
			myDefaultValue !== ''
		) {
			if (from === 'city') {
				return getDefaultValue(
					options,
					myDefaultValueOption?.name.toString(),
					myDefaultValueOption?.label.toString(),
					props.title,
					props.placeholder
				);
			}
			return getDefaultValue(
				options,
				myDefaultValue,
				props.title,
				props.placeholder
			);
		}
		if (options.length > 0 && options[0]) return options[0];
		const labelText =
			props.placeholder ||
			(props.title ? `Select ${props.title}` : 'Select an option');
		return { name: '', label: labelText, placeholder: true };
	};
	const [selected, setSelected] = React.useState<Option>(getInitialSelected());
	const [filter, setFilter] = React.useState('');
	const [inputId, setInputId] = React.useState<string | number | undefined>();
	const [isOpen, setIsOpen] = React.useState(false); // headlessui open (solo desktop)
	const [newOptionLabel, setNewOptionLabel] = React.useState<
		string | undefined
	>(undefined);
	const { register, watch, setValue } = useForm();

	React.useEffect(() => {
		setInputId(undefined);
	}, []);

	const prevSelectedRef = React.useRef<string | undefined>(undefined);
	const prevMyDefaultValueRef = React.useRef(myDefaultValue);
	const prevResetValueRef = React.useRef(resetValue);
	const prevMyDefaultValueOptionRef = React.useRef(myDefaultValueOption);

	React.useEffect(() => {
		if (!selected) return;
		if (prevSelectedRef.current === selected.name) return;
		prevSelectedRef.current = selected.name;

		handleChange && handleChange(selected.name);
		setValueInput && setValueInput(props.name, selected.name);
		resetValueAfterSelecting && setSelected(options[0]);
	}, [selected]);

	React.useEffect(() => {
		const myDefChanged = prevMyDefaultValueRef.current !== myDefaultValue;
		const resetChanged = prevResetValueRef.current !== resetValue;
		const myDefOptChanged =
			prevMyDefaultValueOptionRef.current !== myDefaultValueOption;

		prevMyDefaultValueRef.current = myDefaultValue;
		prevResetValueRef.current = resetValue;
		prevMyDefaultValueOptionRef.current = myDefaultValueOption;

		if (!myDefChanged && !resetChanged && !myDefOptChanged) return;

		if (
			myDefaultValue !== undefined &&
			myDefaultValue !== null &&
			myDefaultValue !== ''
		) {
			if (from === 'city') {
				setSelected(
					getDefaultValue(
						options,
						myDefaultValueOption?.name.toString(),
						myDefaultValueOption?.label.toString(),
						props.title,
						props.placeholder
					)
				);
			} else {
				setSelected(
					getDefaultValue(
						options,
						myDefaultValue,
						props.title,
						props.placeholder
					)
				);
			}
		} else if (resetValue) {
			const fallbackOption =
				options.length > 0 && options[0]
					? options[0]
					: {
							name: '',
							label:
								props.placeholder ||
								(props.title ? `Select ${props.title}` : 'Select an option'),
							placeholder: true,
					  };
			setSelected(fallbackOption);
		}
	}, [myDefaultValue, resetValue, myDefaultValueOption]);

	// Al cerrar (desktop)
	React.useEffect(() => {
		if (!isOpen) {
			setInputId(undefined);
			setValue('input', '');
			setValue('search', '');
			setNewOptionLabel(undefined);
		}
	}, [isOpen]);

	// Al cerrar (mobile Drawer)
	React.useEffect(() => {
		if (!drawerOpen) {
			setInputId(undefined);
			setValue('input', '');
			setValue('search', '');
			setNewOptionLabel(undefined);
			handleSearch && handleSearch('');
			setFilter('');
		}
	}, [drawerOpen]);

	const clearInput = () => setValue('input', '');

	const filteredOptions = options.filter((option: Option) =>
		(option.label as string).toLowerCase().includes(filter.toLowerCase())
	);

	const showCreateIcon =
		filteredOptions.length <= 1 ||
		(filteredOptions.length === 2 &&
			filteredOptions[1].name.toLowerCase() !== filter.toLowerCase());

	const inputValue = watch('search');

	// ⬇️ Reutilizable: render de buscador + lista (lo usamos en desktop y mobile)
	const renderSearchAndList = (onSelect?: (opt: Option) => void) => (
		<>
			{isSearch && (
				<InputText
					ref={inputRef}
					name="search"
					register={register}
					placeholder="Search"
					iconLeft={Icons.search}
					iconRight={showCreateIcon ? Icons.sendPurple : undefined}
					className="w-full 2xl:w-auto px-4"
					classNameIcon={clsx(
						'!top-[28%] md:!top-[25%] !w-4 !h-4',
						props.classNameIcon
					)}
					classNameInput={clsx(
						'pl-9 border-none !bg-gray-15 h-11 md:h-8 !mt-0',
						classNameInput
					)}
					onChange={({ target }) => {
						setValue('search', target.value);
						if (handleSearch) {
							handleSearch(target.value);
							return;
						}
						setFilter(target.value);
					}}
					onFocus={(e) => e.stopPropagation()}
					rightClick={() => {
						setNewOptionLabel(inputValue);
						if (setInputCreate) {
							setValue('search', '');
							setInputCreate(inputValue, clearInput, inputId);
							setInputId(undefined);
						}
					}}
					onKeyDown={(event) => {
						event.stopPropagation();
						if (event.key === 'Enter' && setInputCreate) {
							event.preventDefault();
							const val = (event.target as HTMLInputElement).value;
							setNewOptionLabel(val);
							setInputCreate(val, clearInput, inputId);
						}
					}}
				/>
			)}
			<div
				className={clsx(
					'overflow-auto scroll-custom max-h-72 !rounded-none',
					{ 'mt-3': isSearch },
					classNameList
				)}
			>
				{loading ? (
					<div className="w-full flex justify-center p-2">
						<Spinner type="loadingPage" />
					</div>
				) : (
					filteredOptions.map((option: Option, index: number) => {
						const isLastElement = options.length === index + 1;
						return (
							!option.placeholder && (
								<Listbox.Option
									key={option.name + '-' + index}
									value={option}
									as={React.Fragment}
								>
									{({ active, selected: isSelected }) => (
										<div
											data-city-id={option.name}
											ref={isLastElement ? containerRef : null}
											className={clsx(
												'relative py-3 px-4 cursor-pointer select-none capitalize group',
												active ? 'bg-dark-10' : 'hover:bg-dark-10'
											)}
											onClick={() => {
												if (onSelect) onSelect(option);
											}}
										>
											<div className="flex items-center gap-2">
												<Icon src={option.icon || ''} className="!size-4" />
												<div className={clsx('relative flex gap-1')}>
													<span
														className={clsx(
															'block truncate text-base text-black',
															option.classNameItemLabel
														)}
													>
														{option.label}
													</span>
													<small
														className={clsx('text-purple-150 -mt-1', {
															hidden: option.label !== newOptionLabel,
														})}
													>
														New
													</small>
												</div>
												{isSelected && (
													<span
														className={clsx('absolute right-0 pr-4', {
															'group-hover:hidden': showActionButtons,
														})}
													>
														<CheckIcon
															className="w-5 h-5 text-black"
															aria-hidden="true"
														/>
													</span>
												)}
												<div
													className={clsx(
														'flex items-center gap-2 absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200',
														{
															hidden:
																!showActionButtons ||
																option.label !== newOptionLabel,
														}
													)}
												>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															setValue('input', option.label);
															setValue('search', option.label);
															setInputId(option.name);
															inputRef.current?.focus();
														}}
													>
														<Icon src={Icons.editTwo} className="!size-5" />
													</button>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															setInputId(undefined);
															setValue('input', '');
															if (setInputDelete) setInputDelete(option.name);
														}}
													>
														<Icon
															src={Icons.trash}
															className="!size-5 text-main-red"
														/>
													</button>
												</div>
											</div>
										</div>
									)}
								</Listbox.Option>
							)
						);
					})
				)}
			</div>
		</>
	);

	return (
		<div className={clsx(className)}>
			{/* hidden input para forms */}
			<input
				type="text"
				value={selected?.name}
				className="hidden"
				readOnly
				{...props.register(props.name, props.rules)}
			/>

			<Listbox value={selected} onChange={setSelected}>
				{({ open }) => {
					// Solo sincronizamos isOpen (desktop). En mobile interceptamos el click y usamos Drawer.
					if (!isMobile && open !== isOpen) {
						setTimeout(() => {
							setIsOpen(open);
							onToggleOpen?.(open);
						}, 100);
					}

					return (
						<>
							<Listbox.Label className="block text-left">
								<label
									htmlFor="name"
									className={clsx(
										'text-base text-black relative',
										classNameTitle
									)}
								>
									{props.title}
									{markAsRequired && (
										<Asterisk className="w-3 h-3 absolute -right-3 top-0 text-red-500" />
									)}
								</label>
							</Listbox.Label>

							<div
								className={clsx('relative', {
									'!cursor-not-allowed group': props.disabled,
								})}
							>
								{/* Trigger compartido: en mobile abre Drawer; en desktop funciona normal */}
								<Listbox.Button
									className={clsx(
										'mt-[5px] relative w-full text-left border border-opacity-primary flex focus:outline-none bg-white rounded-full px-6 py-3',
										!selected?.placeholder && 'capitalize',
										{ 'cursor-pointer': !props.disabled },
										{
											'!border-main-red': props.error,
											'pointer-events-none group-hover:!bg-[#F4F4F4A3]':
												props.disabled,
										},
										classNameInput
									)}
									onClick={(e) => {
										if (isMobile) {
											// Evitamos que headlessui abra el popover y usamos Drawer
											e.preventDefault();
											e.stopPropagation();
											setDrawerOpen(true);
										}
									}}
								>
									<span className="flex items-center max-w-[90%]">
										<span
											className={clsx(
												'block truncate text-base text-black',
												{
													'text-opacity-primary':
														selected?.placeholder ||
														props.disabled ||
														!selected?.label,
												},
												classNameLabel
											)}
										>
											{selected?.label
												? selected.label
												: props.placeholder || 'Select'}
										</span>
									</span>
									{clearable && !selected?.placeholder && (
										<span className="absolute inset-y-0 right-10 flex items-center pointer-events-auto">
											<button
												disabled={props.disabled}
												type="button"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													const placeholder = options[0];
													setSelected(placeholder);
													setValueInput && setValueInput(props.name, '');
												}}
												className="text-dark-60 hover:text-black transition-colors"
											>
												<Icon src={Icons.close} width={16} height={16} />
											</button>
										</span>
									)}
									<span className="absolute inset-y-0 right-5 flex items-center ml-3 pointer-events-none">
										{loading && <Spinner className="mr-1" />}
										{/* En mobile mostramos siempre chevron-down (Drawer). En desktop usamos estado de Listbox */}
										{!isMobile ? (
											open ? (
												<ChevronUpIcon
													width={20}
													height={20}
													className="text-black"
												/>
											) : (
												<ChevronDownIcon
													width={20}
													height={20}
													className="text-black"
												/>
											)
										) : (
											<ChevronDownIcon
												width={20}
												height={20}
												className="text-black"
											/>
										)}
									</span>
								</Listbox.Button>

								{/* ▼▼ DESKTOP: Popover original */}
								<div className="hidden md:block">
									<Transition
										show={open}
										as={React.Fragment}
										leave="transition ease-in duration-100"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
										afterLeave={() => {
											handleSearch && handleSearch('');
											setFilter('');
										}}
									>
										<Listbox.Options
											static
											style={{
												width:
													typeof dynamicContainerWidth === 'number'
														? dynamicContainerWidth
														: undefined,
											}}
											className={clsx(
												{ hidden: props.disabled },
												maxHeight,
												'focus:outline-none overflow-hidden scroll-custom',
												inline
													? clsx(
															'relative mt-2 w-full rounded-3xl bg-white border border-opacity-primary p-4',
															classNameInlineList
													  )
													: 'py-4 absolute mt-1 z-30 w-full rounded-lg bg-white border border-opacity-primary shadow-lg'
											)}
										>
											{renderSearchAndList()} {/* reusa mismo render */}
										</Listbox.Options>
									</Transition>
								</div>

								{/* ▼▼ MOBILE: Drawer */}
								<div className="md:hidden">
									<Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
										<DrawerContent>
											<DrawerHeader className="pb-2">
												{props.placeholder && (
													<DrawerDescription className="text-xs">
														{props.placeholder}
													</DrawerDescription>
												)}
											</DrawerHeader>

											<div className="px-2 pb-4">
												{renderSearchAndList((opt) => {
													setSelected(opt);
													setDrawerOpen(false);
												})}
											</div>
										</DrawerContent>
									</Drawer>
								</div>
							</div>
						</>
					);
				}}
			</Listbox>

			{props.error && props.error.message && (
				<ErrorMessageInput
					message={props.error.message}
					withOutIcon={withOutErrorIcon}
				/>
			)}
		</div>
	);
};
